import db from "../db/database";
import { markForDelete, markForSync } from "./syncService";
import {
  documentoIdentifica,
  mismoEspacioDeIdentidad,
  normalizarDatoDeCliente,
  paisDelTerminal,
} from "./identidadDelCliente";
import { getAperturaActiva } from "./cajaEstadoService";
import { usuarioEnSesion } from "./usuarioEnSesion";
import { nuevaLlaveDeAlta } from "../utils/idTpv";

// La regla de cuándo dos filas son la misma persona vive en un solo sitio --
// `identidadDelCliente` --: la comparten el alta y el pull, y tenerla escrita
// dos veces fue justamente lo que dejó al pull con el criterio viejo. Se
// reexporta para no romper a quien ya la importaba de acá.
export { documentoIdentifica, mismoEspacioDeIdentidad, normalizarDatoDeCliente };

// Servicio para manejar las operaciones relacionadas con los clientes

/**
 * Busca un cliente que choque por nombre o por documento.
 *
 * Un solo recorrido para las dos comprobaciones: son 18.643 clientes y esto
 * corre al guardar, no al teclear.
 *
 * @param {{name?: string, Numero_Identificacion_fiscal?: string}} datos
 * @param {{excluyendoId?: number, comprobarNombre?: boolean, comprobarDocumento?: boolean, country?: string}} opciones
 * @returns {Promise<{cliente: object, motivo: "documento"|"nombre"} | null>}
 */
export const buscarClienteDuplicado = async (datos, opciones = {}) => {
  const {
    excluyendoId = null,
    comprobarNombre = true,
    comprobarDocumento = true,
    // Sin país no se pueden resolver los alias del tipo de documento --BC
    // devuelve `ID` donde el formulario guarda `Cedula`-- y se compara por el
    // texto crudo, que es el lado conservador.
    country,
  } = opciones;

  const nombre = comprobarNombre ? normalizarDatoDeCliente(datos.name) : "";
  // Un documento de relleno no identifica a nadie: ver `documentoIdentifica`.
  const documento =
    comprobarDocumento && documentoIdentifica(datos.Numero_Identificacion_fiscal)
      ? normalizarDatoDeCliente(datos.Numero_Identificacion_fiscal)
      : "";

  if (!nombre && !documento) return null;

  const todos = await db.clients.toArray();

  // El documento se mira primero: es la identidad real. Dos personas pueden
  // llamarse igual; dos personas no pueden tener el mismo documento.
  for (const motivo of ["documento", "nombre"]) {
    const buscado = motivo === "documento" ? documento : nombre;
    if (!buscado) continue;

    const choque = todos.find((otro) => {
      if (otro.deleted || otro.id === excluyendoId) return false;
      const suyo =
        motivo === "documento" ? otro.Numero_Identificacion_fiscal : otro.name;
      if (normalizarDatoDeCliente(suyo) !== buscado) return false;

      // El mismo número en dos numeraciones distintas no es la misma persona.
      return motivo === "documento" ? mismoEspacioDeIdentidad(datos, otro, country) : true;
    });

    if (choque) return { cliente: choque, motivo };
  }

  return null;
};

/** Texto del choque, nombrando al cliente que ocupa el dato. */
const motivoDeDuplicado = ({ cliente, motivo }) => {
  const identificacion = cliente.No ? `No. ${cliente.No}` : "todavía sin número";

  return motivo === "documento"
    ? `Ya existe un cliente con ese documento: ${cliente.name || "sin nombre"} (${identificacion}). ` +
        "Selecciónelo en la lista en vez de crearlo de nuevo."
    : `Ya existe un cliente con ese nombre: ${cliente.name} (${identificacion}). ` +
        "Si es otra persona, distíngalos por el documento.";
};

// Agregar un nuevo cliente a la base de datos
export const addClient = async (client) => {
  try {
    const duplicado = await buscarClienteDuplicado(client, {
      country: await paisDelTerminal(),
    });

    if (duplicado) {
      // Un cliente creado en este equipo y todavía sin enviar es el mismo que se
      // está volviendo a cargar: se completa en vez de duplicarlo, y BC nunca
      // llega a ver dos. Un cliente que ya existe en Business Central sí se
      // rechaza -- ahí el alta crearía el duplicado de verdad.
      if (!duplicado.cliente.No) {
        return updateClient(duplicado.cliente.id, { ...client, pendingSync: 1 });
      }

      throw new Error(motivoDeDuplicado(duplicado));
    }

    // La caja desde la que se dio de alta, congelada aquí y sólo aquí.
    //
    // Business Central elige con ella qué plantilla de cliente aplicar: la de
    // la tienda a la que pertenece la caja, y si esa tienda no tiene ninguna,
    // la de Conf. ventas y cobros. Acuerdo `contrato-plantilla-cliente-por-
    // tienda`, vía B. Viaja sola: `transformClientForBC` hace un spread de la
    // fila entera.
    //
    // Va DENTRO del `add` y no en el objeto que se comprueba más arriba. La
    // rama de fusión de duplicado reenvía `client` a `updateClient`, así que
    // ponerla antes le plancharía la caja de hoy a una fila que se creó en
    // otra caja -- justo lo que la regla de congelado quiere evitar.
    //
    // Y se congela en el alta, no en el envío, porque el envío no lee la
    // sesión: manda la fila tal cual salió de Dexie. Un cliente dado de alta
    // sin conexión en la caja A y sincronizado al día siguiente desde la B
    // llevaría la B, y BC aplicaría la plantilla de la tienda equivocada sin
    // que nadie pudiera notarlo.
    //
    // Sin caja abierta viaja vacía, que es un caso legítimo y permanente --el
    // alta desde la pantalla de Clientes--, no un error: BC usa entonces la
    // plantilla de Conf. ventas y cobros.
    const aperturaDelAlta = await getAperturaActiva();

    // Agregar el cliente a la base de datos
    const id = await db.clients.add({
      ...client,
      // La que ya venía manda: si el llamante trae una caja, esa fila ya se
      // congeló antes y volver a resolverla la sustituiría por la de hoy. Hoy
      // ningún llamante la manda -- los dos pasan valores del formulario -- pero
      // el día que exista duplicar cliente, una importación o un reintento que
      // reenvíe la fila leída de Dexie, esto es lo que evita que la caja
      // original se cambie en silencio.
      Caja: client.Caja || aperturaDelAlta?.Caja || "",
      // La tienda del alta, congelada con la misma regla que la caja.
      //
      // Caso 123: sin caja abierta `Caja` viaja vacía, BC aplica la plantilla de
      // Conf. ventas y cobros y, si a esa le faltan dimensiones, el cliente sale
      // «creado» y su primera venta no se registra. Con `Tienda_Alta` BC resuelve
      // la plantilla de esa tienda. BC sólo la lee con `Caja` vacía; con caja
      // abierta viaja igual, porque así la fila dice siempre desde dónde se dio
      // de alta.
      //
      // Sale de la tienda de la apertura activa; sin caja, de la del usuario con
      // sesión --el mismo código que `tpv.No` y que el `Tienda` de BC, ver
      // `utils/cajasDelUsuario.js`--; y si tampoco hay, vacía, como hasta hoy. La
      // sesión se lee al darlo de alta y no al enviarlo, por la misma razón que la
      // caja. La que ya venía manda. Viaja sola por el spread de
      // `transformClientForBC`, y una BC anterior la ignora. Acuerdo
      // `contrato-hallazgos-ciclo-cri-2026-09-14.md`, fila H5 · TPV.
      Tienda_Alta:
        client.Tienda_Alta || aperturaDelAlta?.TPV || usuarioEnSesion()?.Tienda || "",
      // La llave de idempotencia del alta: con ella Business Central reconoce
      // un reintento como «el mismo cliente» y devuelve el existente en vez de
      // crear otro (caso 83: el mismo payload enviado dos veces creaba dos).
      // Nace aquí y sólo aquí, congelada con la misma regla que la Caja: la que
      // ya venía manda. Viaja sola por el spread de `transformClientForBC`.
      // Acuerdo `contrato-idempotencia-y-guardas`.
      Id_Alta: client.Id_Alta || nuevaLlaveDeAlta(aperturaDelAlta?.Caja),
      name: client.name,
      Direccion: client.Direccion || "",
      Direccion_2: client.Direccion_2 || "",
      Poblacion: client.Poblacion || "",
      Cod_Postal: client.Cod_Postal || "",
      Pais: client.Pais || "",
      Telefono: client.Telefono || "",
      Mobil: client.Mobil || "",
      E_Mail: client.E_Mail || "",
      Customer_Price_Group: client.Customer_Price_Group || "",
      Contacto: client.Contacto || "",
      Tipo_Documento_SrI: client.Tipo_Documento_SrI || null,
      Numero_Identificacion_fiscal: client.Numero_Identificacion_fiscal || "",
      POS_Discount: client.POS_Discount || 0,
      createdAt: new Date(),
      pendingSync: 1, // Marcar como pendiente de sincronización (usando 1 en lugar de true)
      lastModified: new Date(),
      No: client.No || null, // Guardar el No de Business Central si existe
      deleted: false,
    });

    // Lo que se devuelve es lo que quedó guardado, no lo que entró.
    //
    // Antes era `{ ...client, id }`, así que faltaba todo lo que el alta pone
    // por su cuenta -- la `Caja` congelada, entre otras cosas -- y la rama de
    // fusión de duplicado de más arriba, que devuelve `getClientById`, sí lo
    // traía. Dos formas distintas para la misma llamada.
    return await getClientById(id);
  } catch (error) {
    console.error("Error al agregar cliente:", error);
    throw error;
  }
};

// Obtener todos los clientes
export const getAllClients = async () => {
  try {
    // Obtener solo clientes no marcados como eliminados
    const clients = await db.clients.filter((client) => !client.deleted).toArray();
    return clients;
  } catch (error) {
    console.error("Error al obtener clientes:", error);
    throw error;
  }
};

// Obtener una vista previa rápida (los primeros `limit`) para pintar la
// pantalla al instante mientras se carga el catálogo completo por atrás.
// Dexie corta el recorrido de la tabla apenas junta `limit` coincidencias,
// así que esto es rápido sin importar el tamaño total de la tabla.
export const getClientsPreview = async (limit = 50) => {
  try {
    const clients = await db.clients
      .filter((client) => !client.deleted)
      .limit(limit)
      .toArray();
    return clients;
  } catch (error) {
    console.error("Error al obtener vista previa de clientes:", error);
    throw error;
  }
};

// Obtener un cliente por ID local
export const getClientById = async (id) => {
  try {
    const client = await db.clients.get(id);
    if (!client || client.deleted) {
      throw new Error("Cliente no encontrado");
    }
    return client;
  } catch (error) {
    console.error(`Error al obtener cliente con ID ${id}:`, error);
    throw error;
  }
};

// Obtener un cliente por No de Business Central
export const getClientByNo = async (no) => {
  try {
    const client = await db.clients
      .where("No")
      .equals(no)
      .and((client) => !client.deleted)
      .first();

    if (!client) {
      throw new Error("Cliente no encontrado");
    }
    return client;
  } catch (error) {
    console.error(`Error al obtener cliente con No ${no}:`, error);
    throw error;
  }
};

export const getClientByNoOrId = async (no) => {
  try {
    let client = await db.clients.get(no);
    if (!client) {
      client = await db.clients
        .where("No")
        .equals(no)
        .and((client) => !client.deleted)
        .first();
    }
    return client;
  } catch (error) {}
};
// Actualizar un cliente existente
export const updateClient = async (id, clientData) => {
  try {
    const client = await db.clients.get(id);
    if (!client || client.deleted) {
      throw new Error("Cliente no encontrado");
    }

    // Sólo se comprueba el dato que CAMBIÓ. Si se comprobaran los dos siempre,
    // los clientes que hoy ya están repetidos quedarían imposibles de guardar:
    // corregirle el teléfono a uno chocaría con su gemelo y no habría forma de
    // salir. Y como se excluye el propio registro, guardar sin tocar el nombre
    // ni el documento nunca se bloquea.
    const cambio = (campo) =>
      clientData[campo] !== undefined &&
      normalizarDatoDeCliente(clientData[campo]) !== normalizarDatoDeCliente(client[campo]);

    const duplicado = await buscarClienteDuplicado(
      // El tipo se lee de la fila cuando la edición no lo trae: si no, una
      // edición de teléfono compararía sin tipo y perdería el alias.
      { ...client, ...clientData },
      {
        excluyendoId: id,
        comprobarNombre: cambio("name"),
        comprobarDocumento: cambio("Numero_Identificacion_fiscal"),
        country: await paisDelTerminal(),
      }
    );

    if (duplicado) {
      throw new Error(motivoDeDuplicado(duplicado));
    }

    // Actualizar el cliente
    await db.clients.update(id, {
      ...clientData,
      lastModified: new Date(),
    });

    // Marcar como pendiente de sincronización
    await markForSync("clients", id);

    return await getClientById(id);
  } catch (error) {
    console.error(`Error al actualizar cliente con ID ${id}:`, error);
    throw error;
  }
};

// Eliminar un cliente
export const deleteClient = async (id) => {
  try {
    const client = await db.clients.get(id);
    if (!client) {
      throw new Error("Cliente no encontrado");
    }

    // Marcar como eliminado y pendiente de sincronización
    await markForDelete("clients", id);

    return true;
  } catch (error) {
    console.error(`Error al eliminar cliente con ID ${id}:`, error);
    throw error;
  }
};
